/**
 * @author: chenlv
 * @date: 2025/01/22
 * @description: Sub Genre Performance 副品类表现卡片组件（横向柱状图）
 */
import React, { useMemo } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import NoData from '@intelligence/components/NoData';
import ChartCard from '@intelligence/v3/components/ChartCard';
import { formatKMBWithPlaceholder } from '@intelligence/v3/utils/number';
import { HorizontalBarChart } from '@intelligence/v3/components/ChartComponents';
import { isEmpty } from '@intelligence/v3/utils/common';
import { getBarOrPieData } from './utils';
import { createHorizontalBarChartTooltipFormatter } from './tooltipFormatters';
import { ChartType } from '@intelligence/v3/enums/chart';
import { METRIC_NAME_I18N_KEY_MAP } from '../../constants';

import type { IChartCardBaseProps } from './types';

/**
 * SubGenrePerformanceCard 组件 Props
 */
interface ISubGenrePerformanceCardProps extends IChartCardBaseProps {
  title: string;
}

/**
 * SubGenrePerformanceCard 副品类表现卡片组件
 * @description 展示当前选中指标的副品类表现横向柱状图
 */
const SubGenrePerformanceCard: React.FC<ISubGenrePerformanceCardProps> = (props) => {
  const {
    style,
    className,
    loading = false,
    title,
    genreSource,
    genreDimension,
    dataSource,
    metric,
    list,
    formatGenreName,
    onHorizontalBarClick,
  } = props;

  const { locale, formatMessage } = useIntl();

  /**
   * 获取横向柱状图数据
   */
  const barData = useMemo<IBarChartData[]>(() => {
    if (loading) {
      return [];
    }

    return getBarOrPieData({
      isHorizontalBar: true,
      genreSource,
      genreDimension,
      dataSource,
      metric,
      list,
    });
  }, [loading, genreSource, genreDimension, dataSource, metric, list]);

  /**
   * 获取指标名称
   */
  const metricName = useMemo(() => {
    const metricKey = METRIC_NAME_I18N_KEY_MAP[metric] || '';
    return formatMessage({ id: metricKey });
  }, [locale, metric]);

  /**
   * 创建横向柱状图 tooltip formatter
   */
  const tooltipFormatter = useMemo(() => {
    return createHorizontalBarChartTooltipFormatter({
      dataSource,
      metric,
      metricName,
      formatGenreName,
    });
  }, [dataSource, metric, metricName, formatGenreName]);

  /**
   * 是否无数据
   */
  const noData = useMemo<boolean>(() => {
    if (loading) {
      return false;
    }

    return isEmpty(barData);
  }, [loading, barData]);

  return (
    <ChartCard
      style={style}
      className={classnames('dbi-sub-genre-performance-card', className)}
      loading={loading}
      title={title}
      genreSource={genreSource}
      chartTypes={[ChartType.Bar]}
      chartType={ChartType.Bar}
    >
      {noData ? (
        <div className="dbi-sub-genre-performance-card__no-data">
          <NoData content={formatMessage({ id: 'common_nodata' })} />
        </div>
      ) : (
        <HorizontalBarChart
          showLabel
          locale={locale as any}
          loading={loading}
          sortByValue={false}
          data={barData}
          xAxisLabelFormatter={formatKMBWithPlaceholder}
          yAxisLabelFormatter={(name: any) => {
            return formatGenreName?.(name) ?? name ?? '';
          }}
          nameFormatter={formatGenreName}
          valueLabelFormatter={formatKMBWithPlaceholder}
          tooltipFormatter={tooltipFormatter}
          onBarClick={onHorizontalBarClick}
        />
      )}
    </ChartCard>
  );
};

export default SubGenrePerformanceCard;
